import React, { useState, useEffect } from 'react';
import { KpiCard, toneColor } from '@/design-system/dna';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Users, GraduationCap, TrendingUp, TrendingDown, AlertTriangle,
  BookOpen, Clock, Award, Target, Activity, Brain, BarChart3,
  Bell, CheckCircle, XCircle, ArrowUp, ArrowDown, Sparkles
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// Cores
const COLORS = {
  purple: '#8E52AB',
  green: '#10B981',
  blue: '#3B82F6',
  orange: '#F59E0B',
  red: '#EF4444',
  pink: '#EC4899'
};

// ============================================
// BLOCO 6: DASHBOARD DE TURMA
// ============================================

export function DashboardTurma({ turmaId, turmaNome }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    if (turmaId) {
      loadDashboard();
    }
  }, [turmaId]);
  
  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dnacore/v2/dashboard-turma/${turmaId}`);
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dashboard da turma');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 bg-slate-700" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-24 bg-slate-700" />
          <Skeleton className="h-24 bg-slate-700" />
          <Skeleton className="h-24 bg-slate-700" />
        </div>
      </div>
    );
  }
  
  if (!data) {
    return (
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardContent className="py-8 text-center">
          <Users className="w-12 h-12 text-purple-300/30 mx-auto mb-3" />
          <p className="text-purple-300/70">Selecione uma turma para ver o dashboard</p>
        </CardContent>
      </Card>
    );
  }
  
  const { kpis, distribuicao, evolucao, top_alunos, alertas } = data;
  
  return (
    <div className="space-y-6" data-testid="dashboard-turma">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">{turmaNome || 'Dashboard da Turma'}</h3>
            <p className="text-purple-300/70 text-sm">{kpis?.total_alunos || 0} alunos matriculados</p>
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={loadDashboard}
          className="border-purple-500/30 text-purple-300"
        >
          Atualizar
        </Button>
      </div>
      
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          title="Score Médio"
          value={kpis?.score_medio?.toFixed(1) || 0}
          icon={Target}
          color="purple"
          subtitle="DNACore"
        />
        <KPICard
          title="Média Geral"
          value={kpis?.media_notas?.toFixed(1) || 0}
          icon={BookOpen}
          color="blue"
          subtitle="Notas"
        />
        <KPICard
          title="Frequência"
          value={`${kpis?.frequencia_media?.toFixed(1) || 0}%`}
          icon={Clock}
          color="green"
          subtitle="Presenças"
        />
        <KPICard
          title="Em Risco"
          value={kpis?.alunos_risco || 0}
          icon={AlertTriangle}
          color="red"
          subtitle="Alunos"
        />
      </div>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribuição por Classificação */}
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              Distribuição por Classificação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distribuicao || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {(distribuicao || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1E293B', 
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Evolução */}
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              Evolução da Turma
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={evolucao || []}>
                  <defs>
                    <linearGradient id="colorTurma" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 92, 246, 0.1)" />
                  <XAxis dataKey="periodo" stroke="rgba(139, 92, 246, 0.5)" tick={{ fontSize: 11 }} />
                  <YAxis stroke="rgba(139, 92, 246, 0.5)" tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }} />
                  <Area type="monotone" dataKey="score" stroke={COLORS.blue} fillOpacity={1} fill="url(#colorTurma)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Top Alunos e Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Alunos */}
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Award className="w-4 h-4 text-yellow-400" />
              Top 5 da Turma
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(top_alunos || []).slice(0, 5).map((aluno, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-700/30">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                      idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-500' : 'bg-slate-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-white text-sm">{aluno.nome}</span>
                  </div>
                  <span className="text-purple-300 font-bold">{aluno.score?.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Alertas */}
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-orange-400" />
              Alertas da Turma
              {alertas?.length > 0 && (
                <Badge className="bg-orange-500 text-white text-xs">{alertas.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alertas?.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {alertas.map((alerta, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white text-sm font-medium">{alerta.aluno}</p>
                      <p className="text-orange-200/70 text-xs">{alerta.mensagem}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-purple-300/70 text-sm">Nenhum alerta ativo</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================
// BLOCO 7: DASHBOARD EXECUTIVO
// ============================================

export function DashboardExecutivo() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [periodo, setPeriodo] = useState('atual');
  
  useEffect(() => {
    loadDashboard();
  }, [periodo]);
  
  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dnacore/v2/dashboard-executivo', {
        params: { periodo }
      });
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-28 bg-slate-700" />
          ))}
        </div>
        <Skeleton className="h-64 bg-slate-700" />
      </div>
    );
  }
  
  const { visao_geral, comparativo_turmas, tendencias, alertas_criticos } = data || {};
  
  return (
    <div className="space-y-6" data-testid="dashboard-executivo">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-600 to-pink-500">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Dashboard Executivo</h3>
            <p className="text-purple-300/70 text-sm">Visão estratégica da instituição</p>
          </div>
        </div>
        
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="w-[150px] bg-slate-700/50 border-purple-500/20 text-purple-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-purple-500/20">
            <SelectItem value="atual" className="text-purple-200">Período Atual</SelectItem>
            <SelectItem value="trimestre" className="text-purple-200">Este Trimestre</SelectItem>
            <SelectItem value="ano" className="text-purple-200">Este Ano</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Visão Geral */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          title="Total de Alunos"
          value={visao_geral?.total_alunos || 0}
          icon={Users}
          color="purple"
          trend={visao_geral?.tendencia_alunos}
        />
        <KPICard
          title="Score Médio"
          value={(visao_geral?.score_medio || 0).toFixed(1)}
          icon={Target}
          color="blue"
          trend={visao_geral?.tendencia_score}
        />
        <KPICard
          title="Taxa Aprovação"
          value={`${(visao_geral?.taxa_aprovacao || 0).toFixed(1)}%`}
          icon={CheckCircle}
          color="green"
        />
        <KPICard
          title="Alertas Críticos"
          value={visao_geral?.alertas_criticos || 0}
          icon={AlertTriangle}
          color="red"
        />
      </div>
      
      {/* Comparativo de Turmas */}
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            Comparativo de Turmas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparativo_turmas || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 92, 246, 0.1)" />
                <XAxis type="number" domain={[0, 100]} stroke="rgba(139, 92, 246, 0.5)" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="turma" stroke="rgba(139, 92, 246, 0.5)" tick={{ fontSize: 11 }} width={100} />
                <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }} />
                <Bar dataKey="score" fill={COLORS.purple} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      
      {/* Tendências e Alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tendências */}
        <Card className="bg-slate-800/50 border-purple-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              Tendências Mensais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={tendencias || []}>
                  <defs>
                    <linearGradient id="colorExec" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.4}/>
                      <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 92, 246, 0.1)" />
                  <XAxis dataKey="mes" stroke="rgba(139, 92, 246, 0.5)" tick={{ fontSize: 10 }} />
                  <YAxis stroke="rgba(139, 92, 246, 0.5)" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }} />
                  <Area type="monotone" dataKey="score" stroke={COLORS.purple} fillOpacity={1} fill="url(#colorExec)" name="Score" />
                  <Area type="monotone" dataKey="frequencia" stroke={COLORS.green} fill="transparent" name="Frequência" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Alertas Críticos */}
        <Card className="bg-slate-800/50 border-red-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              Alertas Críticos
              {alertas_criticos?.length > 0 && (
                <Badge className="bg-red-500 text-white text-xs">{alertas_criticos.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alertas_criticos?.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {alertas_criticos.map((alerta, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium text-sm">{alerta.titulo}</span>
                      <Badge className={`text-xs ${
                        alerta.prioridade === 'alta' ? 'bg-red-500' : 
                        alerta.prioridade === 'media' ? 'bg-orange-500' : 'bg-yellow-500'
                      } text-white`}>
                        {alerta.prioridade}
                      </Badge>
                    </div>
                    <p className="text-red-200/70 text-xs">{alerta.descricao}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                <p className="text-green-300">Nenhum alerta crítico</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================
// COMPONENTE AUXILIAR: KPI CARD
// ============================================

function KPICard({ title, value, icon, color, subtitle }) {
  return <KpiCard id={`db-${String(title).toLowerCase().replace(/\s+/g, '-')}`} icon={icon} label={title} value={value} hint={subtitle} color={toneColor(color)} />;
}

// ============================================
// BLOCO 8: ALERTAS INTELIGENTES
// ============================================

export function AlertasInteligentes() {
  const [loading, setLoading] = useState(true);
  const [alertas, setAlertas] = useState([]);
  const [filtro, setFiltro] = useState('todos');
  
  useEffect(() => {
    loadAlertas();
  }, [filtro]);
  
  const loadAlertas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dnacore/v2/alertas', {
        params: { tipo: filtro !== 'todos' ? filtro : undefined }
      });
      setAlertas(res.data.alertas || []);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const marcarComoLido = async (alertaId) => {
    try {
      await api.put(`/dnacore/v2/alertas/${alertaId}/lido`);
      setAlertas(alertas.filter(a => a.id !== alertaId));
      toast.success('Alerta marcado como lido');
    } catch (error) {
      toast.error('Erro ao atualizar alerta');
    }
  };
  
  const getPrioridadeStyle = (prioridade) => {
    const styles = {
      alta: { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: 'text-red-400' },
      media: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: 'text-orange-400' },
      baixa: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: 'text-yellow-400' }
    };
    return styles[prioridade] || styles.baixa;
  };
  
  return (
    <div className="space-y-6" data-testid="alertas-inteligentes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-orange-600 to-red-500">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Alertas Inteligentes</h3>
            <p className="text-purple-300/70 text-sm">{alertas.length} alertas pendentes</p>
          </div>
        </div>
        
        <Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger className="w-[150px] bg-slate-700/50 border-purple-500/20 text-purple-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-purple-500/20">
            <SelectItem value="todos" className="text-purple-200">Todos</SelectItem>
            <SelectItem value="frequencia" className="text-purple-200">Frequência</SelectItem>
            <SelectItem value="academico" className="text-purple-200">Acadêmico</SelectItem>
            <SelectItem value="comportamental" className="text-purple-200">Comportamental</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Lista de Alertas */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-24 bg-slate-700" />
          ))}
        </div>
      ) : alertas.length === 0 ? (
        <Card className="bg-slate-800/50 border-green-500/20">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h4 className="text-white font-semibold mb-2">Tudo em Ordem!</h4>
            <p className="text-purple-300/60">Não há alertas pendentes no momento</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alertas.map((alerta) => {
            const style = getPrioridadeStyle(alerta.prioridade);
            return (
              <Card key={alerta.id} className={`${style.bg} border ${style.border}`}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className={`w-5 h-5 ${style.icon} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-white font-medium">{alerta.titulo}</h4>
                        <div className="flex items-center gap-2">
                          <Badge className={`${
                            alerta.prioridade === 'alta' ? 'bg-red-500' :
                            alerta.prioridade === 'media' ? 'bg-orange-500' : 'bg-yellow-500'
                          } text-white text-xs`}>
                            {alerta.prioridade}
                          </Badge>
                          <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                            {alerta.tipo}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-purple-200/70 text-sm mb-2">{alerta.mensagem}</p>
                      {alerta.aluno_nome && (
                        <p className="text-purple-300/50 text-xs">Aluno: {alerta.aluno_nome}</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-purple-300/40 text-xs">
                          {new Date(alerta.created_at).toLocaleDateString('pt-BR')}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => marcarComoLido(alerta.id)}
                          className="text-purple-300 hover:text-white hover:bg-purple-500/20 text-xs"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Marcar como lido
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default { DashboardTurma, DashboardExecutivo, AlertasInteligentes };
